import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { StatisticsV2Report, StatisticsV2Request } from './statistics-v2.model';
import { StatisticsV2Service } from './statistics-v2.service';

@Component({
  selector: 'app-statistics-v2',
  standalone: false,
  templateUrl: './statistics-v2.component.html',
  styleUrl: './statistics-v2.component.css'
})
export class StatisticsV2Component {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  reports: any[] = [];
  filteredReports: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  statisticsV2ReportId = 0;
  year = new Date().getFullYear();
  publishDate = this.getTodayIsoDate();
  isPublished = true;
  file: File | null = null;
  filePreview = '';

  titleKh = '';
  titleEn = '';
  searchText = '';

  constructor(
    private statisticsV2Service: StatisticsV2Service,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadReports('');
  }

  private getTodayIsoDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);

      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }
    });
  }

  loadReports(langCode: string) {
    this.statisticsV2Service.getAll(langCode).subscribe((res: any) => {
      const apiData: StatisticsV2Report[] = res.data || [];

      this.reports = apiData
        .filter(item => item.translations && item.translations.length > 0)
        .flatMap(item =>
          item.translations.map((t: any) => ({
            statisticsV2ReportId: item.statisticsV2ReportId,
            year: item.year,
            publishDate: item.publishDate,
            fileUrl: item.fileUrl || item.imageUrl || '',
            isPublished: item.isPublished,
            languageCode: t.languageCode,
            languageId: t.languageId,
            title: t.title
          }))
        );

      this.filterReports(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterReports(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterReports(tab: string) {
    const keyword = this.searchText.trim().toLowerCase();

    this.filteredReports = this.reports.filter(report => {
      const matchesLanguage = report.languageCode === tab;
      const matchesSearch = !keyword ||
        `${report.title ?? ''} ${report.year ?? ''} ${report.publishDate ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesLanguage && matchesSearch;
    });
  }

  onSearchChange() {
    this.currentPage = 1;
    this.filterReports(this.activeTab || 'kh');
  }

  getLanguageCount(lang: string): number {
    return this.reports.filter(report => report.languageCode === lang).length;
  }

  get pagedReports(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredReports.slice(start, end);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  totalPages(): number {
    return Math.ceil(this.filteredReports.length / this.pageSize);
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }

    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.statisticsV2ReportId = id;

    this.statisticsV2Service.getById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;

          this.statisticsV2ReportId = data.statisticsV2ReportId;
          this.year = data.year;
          this.publishDate = data.publishDate?.split('T')[0] || this.getTodayIsoDate();
          this.isPublished = data.isPublished;
          this.file = null;
          this.filePreview = data.fileUrl || data.imageUrl || '';
          this.titleKh = '';
          this.titleEn = '';

          data.translations.forEach((item: any) => {
            if (item.languageCode === 'kh') {
              this.titleKh = item.title;
            } else if (item.languageCode === 'en') {
              this.titleEn = item.title;
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: () => {
        this.exceptionData('Failed to load statistics report data');
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      this.filePreview = URL.createObjectURL(this.file);
    }
  }

  getRequest(statisticsV2ReportId?: number): StatisticsV2Request {
    return {
      file: this.file instanceof File ? this.file : undefined,
      data: {
        statisticsV2ReportId: statisticsV2ReportId ?? 0,
        year: Number(this.year),
        publishDate: this.publishDate,
        isPublished: this.isPublished,
        translations: [
          {
            languageId: 1,
            title: this.titleKh ?? ''
          },
          {
            languageId: 2,
            title: this.titleEn ?? ''
          }
        ]
      }
    };
  }

  resetForm() {
    this.statisticsV2ReportId = 0;
    this.year = new Date().getFullYear();
    this.publishDate = this.getTodayIsoDate();
    this.isPublished = true;
    this.file = null;
    this.filePreview = '';
    this.titleKh = '';
    this.titleEn = '';
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadReports('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest();
    this.clearMsg();

    this.statisticsV2Service.create(request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: () => {
        this.exceptionData('Failed to create statistics report');
      }
    });
  }

  update() {
    this.clearMsg();

    const request = this.getRequest(this.statisticsV2ReportId);
    this.statisticsV2Service.update(this.statisticsV2ReportId, request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: () => {
        this.exceptionData('Failed to update statistics report');
      }
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.statisticsV2Service.delete(this.statisticsV2ReportId).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: () => {
        this.exceptionData('Failed to delete statistics report');
      }
    });
  }

  isImageFile(url: string): boolean {
    if (this.file) {
      return this.file.type.startsWith('image/');
    }

    const cleanUrl = (url || '').split('?')[0].toLowerCase();

    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some(ext => cleanUrl.endsWith(ext));
  }
}
